import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Box,
  Typography,
  Alert,
  IconButton
} from '@mui/material';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { debugLog } from '../../utils/debugLog';

// Declare Moneris global for TypeScript
declare global {
  interface Window {
    monerisCheckout: any;
  }
}

interface MonerisTokenizationProps {
  open: boolean;
  tokenUrl: string;
  ticketId: string;
  onComplete: (ticket: string, dataKey?: string) => void; // FIXED: Support both ticket and optional dataKey
  onCancel: () => void;
}

export const MonerisTokenization: React.FC<MonerisTokenizationProps> = ({
  open,
  tokenUrl,
  ticketId,
  onComplete,
  onCancel
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const checkoutInstanceRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const processedRef = useRef(false); // Prevent duplicate payment processing
  const startedRef = useRef(false); // Prevent duplicate checkout starts
  
  // Load Moneris JavaScript SDK
  useEffect(() => {
    if (!open || !ticketId) return;
    
    debugLog('🔐 MonerisTokenization: Setting up checkout with ticket:', ticketId);
    
    // Determine script URL based on environment
    const scriptUrl = tokenUrl.includes('gatewayt') 
      ? 'https://gatewayt.moneris.com/chkt/js/chkt_v1.00.js'
      : 'https://gateway.moneris.com/chkt/js/chkt_v1.00.js';
    
    // Check if script is already loaded
    if (window.monerisCheckout) {
      setScriptLoaded(true);
      return;
    }
    
    // Load Moneris SDK script
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => {
      debugLog('✅ Moneris SDK loaded');
      setScriptLoaded(true);
    };
    script.onerror = () => {
      console.error('❌ Failed to load Moneris SDK');
      setError('Failed to load payment system');
    };
    
    document.body.appendChild(script);
    
    return () => {
      // Don't remove script as it might be used by other components
    };
  }, [open, ticketId, tokenUrl]);
  
  // Create container div outside React
  useEffect(() => {
    if (!open) return;
    
    // Create a container div outside of React's control
    const portalContainer = document.createElement('div');
    portalContainer.id = 'moneris-portal-container';
    portalContainer.style.position = 'fixed';
    portalContainer.style.top = '0';
    portalContainer.style.left = '0';
    portalContainer.style.width = '100%';
    portalContainer.style.height = '100%';
    portalContainer.style.zIndex = '9999';
    portalContainer.style.display = 'none'; // Hidden by default
    document.body.appendChild(portalContainer);
    
    return () => {
      // Clean up portal container
      if (portalContainer && portalContainer.parentNode) {
        portalContainer.parentNode.removeChild(portalContainer);
      }
    };
  }, [open]);
  
  // PROPER Moneris message listener (following ChatGPT's prescription)
  useEffect(() => {
    if (!open || !ticketId) return;
    
    const safeParse = (v: any) => {
      if (typeof v === 'string') {
        try { return JSON.parse(v); } catch { return null; }
      }
      return v;
    };

    const onMessage = (evt: MessageEvent) => {
      // Origin filtering for security (ChatGPT's recommendation)
      const allowedOrigins = new Set([
        'https://gateway.moneris.com',
        'https://gatewayt.moneris.com'
      ]);
      
      const payload = safeParse(evt.data);
      
      if (!allowedOrigins.has(evt.origin)) {
        // CRITICAL FIX: Log evt.data not payload (which may be null and cause ReferenceError)
        console.warn('🚫 Ignoring message from unauthorized origin:', evt.origin);
        console.warn('   Expected:', Array.from(allowedOrigins));
        console.warn('   Received data:', evt.data);
        return;
      }
      if (!payload || !payload.handler) return;

      debugLog('📬 Moneris message received from', evt.origin, ':', JSON.stringify(payload));

      // Listen for MULTIPLE success events (ChatGPT's recommendation)
      if (payload.handler === 'payment_complete' || 
          payload.handler === 'payment_receipt' ||   // ← THIS is what we're getting!
          payload.handler === 'checkout_complete' ||
          payload.handler === 'transaction_complete') {
        if (processedRef.current) {
          debugLog('🔄 Payment already processed, ignoring duplicate');
          return;
        }
        processedRef.current = true;
        
        debugLog('✅ PAYMENT SUCCESS! Handler:', payload.handler);
        
        // Extract ticket AND dataKey from multiple possible locations (ChatGPT's enhancement)
        const ticket = payload.ticket || payload.sessionId || payload.session_id || ticketId;
        const dataKey = payload.data_key || payload.dataKey || payload.data_key || '';
        
        debugLog('🔑 Extracted from payload:', { ticket, dataKey });
        
        debugLog('🎫 Final ticket for completion:', ticket);
        
        if (ticket && onComplete) {
          // CRITICAL FIX: Pass both ticket and dataKey when available
          onComplete(ticket, dataKey || undefined);
          debugLog('✅ Passed to completion handler:', { ticket, dataKey: dataKey || 'none' });
        } else {
          console.error('❌ No valid ticket found in payment completion');
          setError('Payment processed but no ticket received');
        }
        return;
      }

      // Success by receipt content (ChatGPT's enhanced boolean/string detection)
      const recurSuccess = payload.receipt?.recur_success;
      const isRecurringSuccess = recurSuccess === true || 
                                recurSuccess === 'true' || 
                                recurSuccess === 'TRUE';
      
      if (isRecurringSuccess) {
        if (processedRef.current) {
          debugLog('🔄 Payment already processed via receipt check, ignoring duplicate');
          return;
        }
        processedRef.current = true;
        
        debugLog('✅ PAYMENT SUCCESS detected via receipt! recur_success:', payload.receipt.recur_success);
        
        const ticket = payload.ticket || payload.sessionId || payload.session_id || ticketId;
        const dataKey = payload.data_key || payload.dataKey || payload.receipt?.data_key || '';
        
        if (ticket && onComplete) {
          onComplete(ticket, dataKey || undefined);
          debugLog('✅ Passed to completion handler via receipt:', { ticket, dataKey: dataKey || 'none' });
        } else {
          console.error('❌ No valid ticket found in receipt success');
          setError('Payment complete but no ticket received');
        }
        return;
      }

      if (payload.handler === 'payment_cancelled' || 
          payload.handler === 'close_checkout' || 
          payload.handler === 'cancel_checkout') {
        if (!processedRef.current && onCancel) {
          debugLog('❌ Payment cancelled by user');
          onCancel();
        }
        return;
      }

      if (payload.handler === 'page_loaded') {
        debugLog('📄 Checkout page loaded:', JSON.stringify(payload));
        setLoading(false);
        // DO NOT AUTO-CLOSE! Wait for payment_complete
        return;
      }
      
      // CHATGPT'S ENHANCEMENT: Log unexpected message types for debugging
      debugLog('📌 Ignoring Moneris message:', payload.handler);
      debugLog('   Full payload for debugging:', payload);
      
      // Log if this looks like a success message we might be missing
      if (payload.handler && payload.handler.toLowerCase().includes('success')) {
        console.warn('⚠️ Potential success message not handled:', payload.handler, payload);
      }
    };

    debugLog('👂 Setting up robust Moneris listener');
    window.addEventListener('message', onMessage, { passive: true });

    return () => {
      debugLog('🧹 Cleaning up Moneris listener');
      window.removeEventListener('message', onMessage);
    };
  }, [ticketId, onComplete, onCancel, open]);
  
  // Initialize Moneris Checkout when script is loaded
  useEffect(() => {
    if (!scriptLoaded || !open || !ticketId) return;
    
    // Add a delay to ensure everything is ready
    const initTimeout = setTimeout(() => {
      const container = containerRef.current;
      if (!container) {
        console.error('❌ Container ref not found');
        setError('Failed to initialize payment form');
        return;
      }
      
      debugLog('🎫 Initializing Moneris Checkout with ticket:', ticketId);
      
      // Create a unique div ID for this instance
      const uniqueId = `moneris-checkout-${Date.now()}`;
      
      // Create the checkout div
      const checkoutDiv = document.createElement('div');
      checkoutDiv.id = uniqueId;
      
      // Clear container and add the new div
      container.innerHTML = '';
      container.appendChild(checkoutDiv);
      
      try {
        // Initialize Moneris Checkout
        const myCheckout = new window.monerisCheckout();
        checkoutInstanceRef.current = myCheckout;
        
        // Set mode based on environment
        const mode = tokenUrl.includes('gatewayt') ? 'qa' : 'prod';
        myCheckout.setMode(mode);
        
        // Set checkout div by ID
        myCheckout.setCheckoutDiv(uniqueId);
        
        // Note: We're now using window message listener instead of SDK callbacks
        // This provides more reliable event handling
        
        // Start checkout with ticket (guard against double-start)
        if (!startedRef.current) {
          startedRef.current = true;
          debugLog('🚀 Starting checkout with ticket:', ticketId);
          myCheckout.startCheckout(ticketId);
        } else {
          debugLog('⚠️ Checkout already started, skipping duplicate start');
        }
        
      } catch (err: any) {
        console.error('❌ Failed to initialize checkout:', err);
        setError('Failed to initialize payment system: ' + err.message);
      }
    }, 500); // Give more time for everything to stabilize
    
    return () => {
      clearTimeout(initTimeout);
      // Reset refs on cleanup
      processedRef.current = false;
      startedRef.current = false;
    };
  }, [scriptLoaded, open, ticketId, tokenUrl]);
  
  const handleClose = () => {
    debugLog('🚪 Closing tokenization dialog');
    onCancel();
  };
  
  return (
    <Dialog 
      open={open} 
      maxWidth="md" 
      fullWidth
      onClose={(event, reason) => {
        // Prevent accidental closes (ChatGPT's recommendation)
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          debugLog('🚫 Preventing accidental dialog close via', reason);
          return;
        }
        handleClose();
      }}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          minHeight: '500px'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="span">Enter payment information</Typography>
          <IconButton onClick={handleClose} size="small" aria-label="Close payment form">
            <CloseOutlined />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ minHeight: '400px', position: 'relative' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {/* Loading indicator */}
          {loading && (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center" 
              minHeight="350px"
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading secure payment form...
              </Typography>
            </Box>
          )}
          
          {/* Container for Moneris Checkout - isolated from React */}
          <Box
            ref={containerRef}
            sx={{
              display: loading ? 'none' : 'block',
              minHeight: '400px',
              width: '100%',
              position: 'relative',
              '& > div': {
                width: '100%',
                minHeight: '400px'
              }
            }}
          />
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Your payment information is securely processed by Moneris.
            We never store your card details on our servers.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};